/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";

const Disclaimer = () => (
  <div className="my-7 p-5 rounded-2xl border border-[#bc6c25]/30 bg-[#bc6c25]/5 text-[#1a2421] text-sm leading-relaxed">
    <strong>Aviso médico importante:</strong> este artículo es informativo. No sustituye consulta médica ni tratamiento prescrito. <strong>No suspendas medicamentos</strong> sin hablar antes con tu profesional de salud.
  </div>
);

const Callout = ({ children }) => (
  <div className="my-7 p-5 border-l-4 border-[#bc6c25] bg-[#bc6c25]/5 rounded-r-2xl text-[#1a2421] text-[15px] leading-relaxed">
    {children}
  </div>
);

const TOC = ({ items }) => (
  <nav className="my-7 p-5 bg-[#f0ebe1] rounded-2xl border border-[#2c4a3b]/8 not-prose">
    <p className="text-[10px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold">En este artículo</p>
    <ol className="mt-3 space-y-1.5 text-sm text-[#1a2421]">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2">
          <span className="text-[#bc6c25] font-bold">{i + 1}.</span>
          <a href={`#${it.id}`} className="hover:text-[#bc6c25]">{it.label}</a>
        </li>
      ))}
    </ol>
  </nav>
);

const Esperanza = () => (
  <div className="my-9 p-7 rounded-3xl bg-gradient-to-br from-[#1a2421] to-[#2c4a3b] text-[#f9f6f0] not-prose">
    <p className="text-[10px] uppercase tracking-[0.28em] text-[#bc6c25] font-semibold">Eliora Longevity</p>
    <h3 className="font-serif text-2xl md:text-3xl mt-2 leading-tight">Si tienes vida, tienes esperanza.</h3>
    <p className="text-sm text-[#f9f6f0]/75 mt-3 leading-relaxed">
      Hagamos juntas un protocolo personalizado. 3 minutos. Gratis. Solo necesitas atreverte.
    </p>
    <Link to="/#quiz" className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#bc6c25] hover:bg-[#9c581e] text-white text-sm font-semibold">
      Hacer el Quiz Wellness →
    </Link>
  </div>
);

export const ARTICLE_CONTENT_6 = {
  /* 20. Hígado graso */
  "higado-graso-no-alcoholico-nafld": (
    <>
      <p>El hígado graso no alcohólico (NAFLD) es la enfermedad hepática más prevalente en México. Se estima que <strong>1 de cada 3 adultos mexicanos</strong> la tiene, muchos sin saberlo. Es silenciosa: no duele hasta etapas avanzadas. Pero cuando se manifiesta, puede progresar a cirrosis e insuficiencia hepática.</p>
      <p>La buena noticia: en etapas iniciales-medias, es <strong>reversible</strong>. Y el glutatión es protagonista de esa reversibilidad.</p>

      <Disclaimer />

      <TOC items={[
        { id: "que-es", label: "Qué es y por qué es tan común" },
        { id: "gsh-higado", label: "El glutatión es del hígado" },
        { id: "señales", label: "Señales tempranas" },
        { id: "protocolo", label: "Protocolo de detox real" },
      ]} />

      <h2 id="que-es">Qué es y por qué es tan común</h2>
      <p>NAFLD es acumulación de grasa en el hígado &gt;5 % de su peso. Causas modernas: dieta alta en fructosa (refrescos, jugos), sedentarismo, resistencia a la insulina, sobrepeso visceral. México combina las cuatro al máximo.</p>

      <h2 id="gsh-higado">El glutatión es del hígado</h2>
      <p>El hígado tiene la <strong>concentración más alta de glutatión</strong> de todo el cuerpo. Lo usa para Fase II de detoxificación: cada toxina, fármaco, alcohol, pesticida, hormona vieja se conjuga con glutatión para eliminarse.</p>
      <p>Cuando hay hígado graso, la inflamación local quema glutatión a un ritmo brutal. <Link to="/immunocal">Immunocal</Link> aporta el aminoácido limitante (cisteína) para fabricarlo.</p>

      <Callout>
        Estudios en <em>Journal of Hepatology</em> muestran que pacientes con NAFLD que suplementan precursores de glutatión + dieta mediterránea revierten infiltración grasa significativamente en 6 meses.
      </Callout>

      <h2 id="señales">Señales tempranas</h2>
      <ul>
        <li>Fatiga sin razón.</li>
        <li>Cintura &gt;90 cm (mujer) o &gt;100 cm (hombre).</li>
        <li>ALT/AST levemente elevadas en análisis.</li>
        <li>Niebla mental que empeora tras comer.</li>
        <li>Sensibilidad creciente a alcohol/medicamentos.</li>
      </ul>

      <h2 id="protocolo">Protocolo de detox real</h2>
      <ol>
        <li><strong>Eliminar:</strong> azúcar y fructosa (refrescos, jugos, dulces). Esto solo revierte hígado graso en muchos casos.</li>
        <li>Bajar ultraprocesados.</li>
        <li>Caminar 10 min después de cada comida.</li>
        <li><Link to="/immunocal">Immunocal</Link> 1-2 sobres/día (precursor de glutatión).</li>
        <li>Omega-3 EPA/DHA &gt;2 g/día.</li>
        <li>Café negro 1-2 tazas/día (asociado a menor riesgo de NAFLD).</li>
        <li>Pérdida de 7-10 % del peso corporal: el factor #1 de reversión.</li>
        <li>Cero alcohol durante el período de reversión.</li>
      </ol>

      <Esperanza />
    </>
  ),

  /* 21. Síndrome metabólico */
  "sindrome-metabolico-y-obesidad": (
    <>
      <p>El "síndrome metabólico" suena técnico, pero la realidad es simple: es la combinación de obesidad central + presión alta + glucosa alterada + triglicéridos altos + HDL bajo. En México, <strong>1 de cada 3 adultos</strong> lo cumple. Es la antesala silenciosa de diabetes, infarto, ictus.</p>

      <Disclaimer />

      <TOC items={[
        { id: "que-es", label: "Qué es el síndrome metabólico" },
        { id: "raíz", label: "La raíz común: resistencia a la insulina" },
        { id: "gsh-rol", label: "El rol del glutatión" },
        { id: "estrategia", label: "Estrategia integral 90 días" },
      ]} />

      <h2 id="que-es">Qué es el síndrome metabólico</h2>
      <p>Cumples criterios si tienes <strong>3 o más</strong> de:</p>
      <ul>
        <li>Cintura &gt;88 cm (mujer) / &gt;102 cm (hombre).</li>
        <li>Presión &gt;130/85 mmHg.</li>
        <li>Glucosa en ayunas &gt;100 mg/dL.</li>
        <li>Triglicéridos &gt;150 mg/dL.</li>
        <li>HDL &lt;50 (mujer) / &lt;40 (hombre).</li>
      </ul>

      <h2 id="raíz">La raíz común: resistencia a la insulina</h2>
      <p>Todos los criterios tienen un denominador común: las células no escuchan bien a la insulina. La grasa visceral acumulada libera citoquinas inflamatorias que <em>causan</em> la resistencia. Es un círculo vicioso que solo se rompe con intervención sostenida.</p>

      <h2 id="gsh-rol">El rol del glutatión</h2>
      <p>La inflamación crónica del síndrome metabólico vacía glutatión sistémico. Restaurar precursores ayuda a romper el círculo: menos inflamación → mejor sensibilidad a insulina → menos grasa visceral.</p>

      <Callout>
        Estudios muestran que mejorar glutatión + intervenciones de estilo de vida + tratamiento médico apropiado revierte síndrome metabólico en 60-70 % de casos en 6-12 meses.
      </Callout>

      <h2 id="estrategia">Estrategia integral 90 días</h2>
      <ol>
        <li><strong>Mediciones iniciales:</strong> cintura, peso, presión, panel metabólico.</li>
        <li><strong>Alimentación:</strong> elimina ultraprocesados, azúcares, harinas refinadas. Aumenta proteína, vegetales, grasas buenas.</li>
        <li><strong>Ayuno 14/10</strong> si tu contexto lo permite.</li>
        <li><strong>Caminar 30 min/día + fuerza 2x/semana.</strong></li>
        <li><Link to="/immunocal">Immunocal</Link> 1-2 sobres/día.</li>
        <li>Omega-3 + magnesio + vitamina D3 (ver <Link to="/blog/omega-gen-v-5-en-1">guía Omega Gen V</Link>).</li>
        <li>Reevaluar a los 90 días con análisis.</li>
      </ol>

      <Esperanza />
    </>
  ),

  /* 22. Artritis */
  "artritis-dolor-articular-cronico": (
    <>
      <p>Despertar con manos rígidas, dolor en rodillas al subir escaleras, articulaciones que crujen, hinchazón que aparece y se va. Si esto te suena, sabes que la artritis (o el dolor articular crónico sin diagnóstico claro) puede arrastrarte energéticamente. Te explico cómo se conecta con el glutatión y qué puedes hacer.</p>

      <Disclaimer />

      <TOC items={[
        { id: "tipos", label: "Tipos de artritis" },
        { id: "inflamacion", label: "El componente inflamatorio" },
        { id: "gsh", label: "Glutatión, omega-3 y dolor" },
        { id: "protocolo", label: "Protocolo antinflamatorio integral" },
      ]} />

      <h2 id="tipos">Tipos de artritis</h2>
      <ul>
        <li><strong>Osteoartritis (desgaste):</strong> la más común, asociada a edad y sobreuso.</li>
        <li><strong>Artritis reumatoide:</strong> autoinmune, requiere tratamiento médico.</li>
        <li><strong>Artritis psoriásica</strong>, <strong>espondilitis</strong>, <strong>gota</strong>: cada una con manejo específico.</li>
      </ul>

      <h2 id="inflamacion">El componente inflamatorio</h2>
      <p>Toda artritis tiene un componente inflamatorio. La diferencia es la causa. Pero el manejo del componente inflamatorio sistémico ayuda en TODAS — independiente del diagnóstico.</p>

      <h2 id="gsh">Glutatión, omega-3 y dolor</h2>
      <p>El glutatión modula la liberación de citoquinas inflamatorias en las articulaciones. Los omega-3 EPA/DHA producen resolvinas que <em>apagan</em> la inflamación. Combinar ambos puede reducir el uso de analgésicos significativamente —pero <strong>no suspendas medicación sin orden médica</strong>.</p>

      <Callout>
        Un meta-análisis en <em>Pain</em> mostró que omega-3 EPA/DHA en dosis &gt;2.7 g/día reduce dolor articular comparable a AINEs leves, sin los efectos secundarios gástricos.
      </Callout>

      <h2 id="protocolo">Protocolo antinflamatorio integral</h2>
      <ol>
        <li><Link to="/immunocal">Immunocal</Link> 1 sobre/día.</li>
        <li><Link to="/blog/omega-gen-v-5-en-1">Omega Gen V</Link> con cada comida principal.</li>
        <li>Cúrcuma con piperina diaria (cocina o suplemento 500 mg).</li>
        <li>Eliminar 30 días: gluten, lácteos industriales, ultraprocesados, exceso de azúcar.</li>
        <li>Frutos rojos diarios (1 taza).</li>
        <li>Movimiento suave constante: caminar, nadar, yoga. NO sedentarismo.</li>
        <li>Vitamina D3 a 50-80 ng/ml.</li>
        <li>Manejo de estrés (cortisol crónico empeora artritis).</li>
      </ol>

      <Esperanza />
    </>
  ),

  /* 23. Fibromialgia */
  "fibromialgia-fatiga-cronica": (
    <>
      <p>Vivir con fibromialgia o fatiga crónica es vivir con un nivel de cansancio que el sedentario común no entiende. No es "estar cansada" — es un agotamiento que no se quita durmiendo, acompañado de dolor difuso y sensibilidad amplificada. Mucha gente pasa años buscando diagnóstico antes de tener nombre.</p>

      <Disclaimer />

      <TOC items={[
        { id: "que-es", label: "Qué son y por qué se confunden" },
        { id: "mitocondrias", label: "El problema mitocondrial" },
        { id: "gsh", label: "Glutatión y fatiga" },
        { id: "protocolo", label: "Protocolo de soporte (cuidadoso)" },
      ]} />

      <h2 id="que-es">Qué son y por qué se confunden</h2>
      <p>La <strong>fibromialgia</strong> se caracteriza por dolor crónico difuso, fatiga, sueño no reparador, niebla mental. El <strong>síndrome de fatiga crónica (SFC/EM)</strong> tiene fatiga como síntoma central, empeora con esfuerzo. Hay solapamiento entre ambos.</p>

      <h2 id="mitocondrias">El problema mitocondrial</h2>
      <p>Estudios consistentes muestran <strong>disfunción mitocondrial</strong> en ambas condiciones. Las mitocondrias (centrales energéticas de tus células) producen menos ATP, lo que se traduce literalmente en menos batería disponible.</p>

      <h2 id="gsh">Glutatión y fatiga</h2>
      <p>Las mitocondrias dañadas producen más radicales libres y dependen más del glutatión para mantenerse funcionales. Niveles bajos de glutatión perpetúan la disfunción.</p>

      <Callout>
        <strong>Importante:</strong> en fibromialgia/SFC, "más es peor". El ejercicio intenso, los retos extremos, los protocolos agresivos pueden generar crisis. Avanza despacio, con paciencia.
      </Callout>

      <h2 id="protocolo">Protocolo de soporte (cuidadoso)</h2>
      <ol>
        <li>Acompañamiento médico (reumatología/medicina interna).</li>
        <li><Link to="/immunocal">Immunocal</Link> 1 sobre/día, empezar con MEDIO sobre la primera semana.</li>
        <li>CoQ10 100-200 mg/día (soporte mitocondrial directo).</li>
        <li>Magnesio glicinato 400 mg antes de dormir.</li>
        <li>Vitamina D3 y B12 a niveles óptimos.</li>
        <li>Omega-3 EPA/DHA.</li>
        <li>Movimiento muy suave (caminata 10 min, yoga restaurativa).</li>
        <li>Higiene de sueño estricta.</li>
        <li>Reducir estímulos (pantallas, ruido, multitarea).</li>
      </ol>

      <Esperanza />
    </>
  ),

  /* 24. Ansiedad y eje intestino-cerebro */
  "ansiedad-depresion-eje-intestino-cerebro": (
    <>
      <p>Si tienes ansiedad o depresión —diagnosticada o no— probablemente has escuchado el discurso clásico: "es química cerebral, falta serotonina, toma esto". Pero hay algo que la psiquiatría está empezando a reconocer en los últimos años: <strong>el 90 % de la serotonina del cuerpo se produce en el intestino</strong>, no en el cerebro. Y la salud intestinal afecta directamente la salud mental.</p>

      <Disclaimer />

      <TOC items={[
        { id: "eje", label: "El eje intestino-cerebro" },
        { id: "microbiota", label: "Microbiota y salud mental" },
        { id: "inflamacion", label: "Inflamación silenciosa y ánimo" },
        { id: "gsh", label: "Glutatión y cerebro" },
        { id: "protocolo", label: "Protocolo integral" },
      ]} />

      <h2 id="eje">El eje intestino-cerebro</h2>
      <p>Tu intestino y cerebro se comunican constantemente vía nervio vago, hormonas y citoquinas. Un intestino inflamado o disbiótico envía señales que <em>cambian</em> el ánimo, la ansiedad y la cognición.</p>

      <h2 id="microbiota">Microbiota y salud mental</h2>
      <p>Estudios consistentes muestran microbiota distinta en personas con depresión vs sanas. Suplementación con probióticos específicos (psicobióticos) ha mostrado reducir ansiedad en pacientes con SII.</p>

      <h2 id="inflamacion">Inflamación silenciosa y ánimo</h2>
      <p>La inflamación crónica de bajo grado <em>activa</em> la microglía cerebral, reduce la creación de nuevas neuronas, y se asocia con depresión. Por eso muchos pacientes con depresión tienen PCR elevada.</p>

      <h2 id="gsh">Glutatión y cerebro</h2>
      <p>Niveles bajos de glutatión cerebral se asocian con mayor severidad de síntomas depresivos. El glutatión protege neuronas, modula inflamación cerebral, recicla otros antioxidantes.</p>

      <Callout>
        <strong>Aviso:</strong> si tomas antidepresivos o ansiolíticos, sigue tomándolos. La suplementación no reemplaza tratamiento. Habla con tu psiquiatra antes de cualquier cambio.
      </Callout>

      <h2 id="protocolo">Protocolo integral</h2>
      <ol>
        <li><strong>Continúa cualquier tratamiento psiquiátrico/psicológico.</strong></li>
        <li><Link to="/immunocal">Immunocal</Link> 1 sobre/día.</li>
        <li>Omega-3 EPA &gt;1 g/día (evidencia robusta en depresión).</li>
        <li>Vitamina D3 a niveles óptimos (deficiencia correlaciona con depresión).</li>
        <li>Magnesio glicinato 200-400 mg/noche.</li>
        <li>Probióticos de calidad 60 días.</li>
        <li>Alimentación: alta en fibra, fermentados, omega-3, baja en ultraprocesados.</li>
        <li>Movimiento diario al sol matutino.</li>
        <li>Sueño consistente 7-9h.</li>
        <li>Conexión humana real (la soledad es un factor inflamatorio enorme).</li>
      </ol>

      <Esperanza />
    </>
  ),
};

export default ARTICLE_CONTENT_6;
